import { DateTime } from '@ksyos/date-time';
import {
    Accordion,
    AccordionContent,
    AccordionContentIndent,
    AccordionDescriptionDetails,
    AccordionDescriptionList,
    AccordionDescriptionTerm,
    AccordionItem,
    AccordionLink,
    AccordionTrigger,
    Button,
} from '@ksyos/design-system';
import { useLocation } from '@ksyos/router';
import { isEmpty } from 'lodash';
import { lazy, Suspense, useContext, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import type { IPatient } from '@common/interfaces/patient';
import type { IReduxState } from '@frontend/configureStore';
import { EpisodeCategory, JobStatus, ManagerAuthorization, OrganizationType, Profession } from '@common/enums';
import { getFormattedAddress } from '@common/utils/getFormattedAddress';
import { Loader } from '@frontend/components/elements/common';
import { DisplayFullName } from '@frontend/components/elements/DisplayFullName';
import { translateKey } from '@frontend/translate';
import { hasOneAuthorization, useAbortableFetch } from '@frontend/utils';
import { getCountryName } from '@frontend/utils/countriesUtils';
import { useUserLocalStorage } from '@frontend/utils/hooks/useUserLocalStorage';
import { AllergiesShortList } from '../allergies/AllergiesShortList';
import { EpisodeJournalShortList } from '../episodeJournal/EpisodeJournalShortList';
import { LabResultsShortList } from '../labResults/LabResultsShortList';
import { MedicationsShortList } from '../medications/MedicationsShortList';
import { PatientDownloadMedicalRecordOriginal } from '../PatientDownloadMedicalRecordOriginal';
import { PatientActiveInsuranceContext } from '../PatientInsuranceHistoryContext';

const EditPatientModal = lazy(() =>
    import('../EditPatientModal').then((module) => ({ default: module.EditPatientModal }))
);
const PatientEditCareProviderModal = lazy(() =>
    import('./PatientEditCareProviderModal').then((module) => ({ default: module.PatientEditCareProviderModal }))
);

interface IPatientSideBarProps {
    episodeCategory?: EpisodeCategory | null;
    onClickShowViewMedicalDossierPatientConsentModal?: () => void;
    onReload: () => Promise<void>;
    patient: IPatient;
    patientHasMedicalHistory?: boolean;
}

export function PatientSideBar(props: IPatientSideBarProps) {
    const abortableFetch = useAbortableFetch();

    const nextCycleTimer = useRef<number | null>(null);

    const { activeInsurance } = useContext(PatientActiveInsuranceContext);

    const practitionerId = useSelector((state: IReduxState) => state.authentication.id);
    const isKsyos = useSelector((state: IReduxState) => state.authentication.isKsyos);
    const isManager = useSelector((state: IReduxState) => state.authentication.isManager);
    const organizationId = useSelector((state: IReduxState) => state.authentication.organizationId);
    const organizationType = useSelector((state: IReduxState) => state.authentication.organizationType);
    const isPolimedewerker = useSelector(
        (state: IReduxState) => state.authentication.profession === Profession.outpatientAssistant
    );

    const [accordionValue, setAccordionValue] = useUserLocalStorage(
        'patientSideBar_accordion',
        isKsyos || isPolimedewerker
            ? ['patient-info']
            : props.patient.careProviderPractitionerId !== practitionerId
              ? ['practitioner-info']
              : undefined
    );
    const [importCycleStatus, setImportCycleStatus] = useState({ dateTime: DateTime.now(), isRunning: true });
    const [showEditCareProviderModal, setShowEditCareProviderModal] = useState(false);
    const [isEditPatientModalOpen, setIsEditPatientModalOpen] = useState(false);
    const [hasPatientConsentHistory, setHasPatientConsentHistory] = useState(false);
    const location = useLocation();

    useEffect(() => {
        (async () => {
            if (organizationType === OrganizationType.gpPractice) {
                await loadCurrentConsent();
            }

            checkImportStatus();
        })();

        return () => {
            stopCheckCycle();
            abortableFetch.abort();
        };
    }, []);

    useEffect(() => {
        stopCheckCycle();

        if (importCycleStatus.isRunning) {
            nextCycleTimer.current = window.setTimeout(() => {
                checkImportStatus();
            }, 5000);
        }
    }, [importCycleStatus]);

    useEffect(() => {
        loadCurrentConsent();
    }, [props.patientHasMedicalHistory]);

    return (
        <>
            {props.patientHasMedicalHistory && props.patient.authorization?.canUpdatePatientConsent && (
                <Button
                    onClick={() => {
                        if (props.onClickShowViewMedicalDossierPatientConsentModal) {
                            props.onClickShowViewMedicalDossierPatientConsentModal();
                        }
                    }}
                    sx={{ mb: 3 }}
                    variant="outline"
                    width="full"
                >
                    {translateKey('exchangePatientData.accessMedicalHistory')}
                </Button>
            )}
            <Accordion
                defaultValue={accordionValue}
                onValueChange={setAccordionValue}
                type="multiple"
            >
                <AccordionItem value="practitioner-info">
                    <AccordionTrigger icon="practitioner">{translateKey('patient.careProvider')}</AccordionTrigger>
                    <AccordionContent>
                        <AccordionContentIndent>
                            <AccordionDescriptionList>
                                {props.patient.careProviderPractitionerId ? (
                                    <>
                                        <AccordionDescriptionTerm>
                                            {translateKey('common.name')}
                                        </AccordionDescriptionTerm>
                                        <AccordionDescriptionDetails>
                                            <DisplayFullName {...props.patient.careProviderPractitioner} />
                                        </AccordionDescriptionDetails>
                                    </>
                                ) : (
                                    <AccordionDescriptionDetails>
                                        {translateKey('patient.careProvider_unknown')}
                                    </AccordionDescriptionDetails>
                                )}
                                {props.patient.careProviderOrganization?.name && (
                                    <>
                                        <AccordionDescriptionTerm>
                                            {translateKey('patient.careProviderOrganization')}
                                        </AccordionDescriptionTerm>
                                        <AccordionDescriptionDetails>
                                            {props.patient.careProviderOrganization.name}
                                        </AccordionDescriptionDetails>
                                    </>
                                )}
                                {props.patient.careProviderOrganization?.phoneNumber && (
                                    <>
                                        <AccordionDescriptionTerm>
                                            {translateKey('patient.practiceNumber')}
                                        </AccordionDescriptionTerm>
                                        <AccordionDescriptionDetails>
                                            {props.patient.careProviderOrganization.phoneNumber}
                                        </AccordionDescriptionDetails>
                                    </>
                                )}
                                {props.patient.careProviderOrganization?.emergencyNumber && (
                                    <>
                                        <AccordionDescriptionTerm>
                                            {translateKey('patient.practiceEmergencyNumber')}
                                        </AccordionDescriptionTerm>
                                        <AccordionDescriptionDetails>
                                            {props.patient.careProviderOrganization.emergencyNumber}
                                        </AccordionDescriptionDetails>
                                    </>
                                )}
                            </AccordionDescriptionList>
                            {(props.patient.authorization?.canUpdate ||
                                hasOneAuthorization(ManagerAuthorization.patientEdit)) && (
                                <AccordionLink
                                    onClick={async () => setShowEditCareProviderModal(true)}
                                    variant="button"
                                >
                                    {props.patient.careProviderPractitionerId
                                        ? translateKey('patient.editCareProvider')
                                        : translateKey('patient.addCareProvider')}
                                </AccordionLink>
                            )}
                        </AccordionContentIndent>
                    </AccordionContent>
                </AccordionItem>

                <AccordionItem value="patient-info">
                    <AccordionTrigger icon="patient">{translateKey('patient.detailsCounselling')}</AccordionTrigger>
                    <AccordionContent>
                        <AccordionContentIndent>
                            <AccordionDescriptionList>
                                {props.patient.identifierSocialSecurity && (
                                    <>
                                        <AccordionDescriptionTerm>
                                            {translateKey('patient.identifierSocialSecurity')}
                                        </AccordionDescriptionTerm>
                                        <AccordionDescriptionDetails>
                                            {props.patient.identifierSocialSecurity}
                                        </AccordionDescriptionDetails>
                                    </>
                                )}
                                {props.patient.maritalStatus && (
                                    <>
                                        <AccordionDescriptionTerm>
                                            {translateKey('patient.maritalStatus')}
                                        </AccordionDescriptionTerm>
                                        <AccordionDescriptionDetails>
                                            {translateKey(`maritalStatusEnum.${props.patient.maritalStatus}`)}
                                        </AccordionDescriptionDetails>
                                    </>
                                )}
                                {(props.patient.street || props.patient.zip || props.patient.city) && (
                                    <>
                                        <AccordionDescriptionTerm>
                                            {translateKey('patient.address')}
                                        </AccordionDescriptionTerm>
                                        <AccordionDescriptionDetails>
                                            <div>{getFormattedAddress(props.patient)}</div>
                                            <div>
                                                {props.patient.zip}
                                                &ensp;
                                                {props.patient.city?.toUpperCase()}
                                            </div>
                                            {props.patient.country && (
                                                <div>{getCountryName(props.patient.country).toUpperCase()}</div>
                                            )}
                                        </AccordionDescriptionDetails>
                                    </>
                                )}
                                {props.patient.homePhoneNumber && (
                                    <>
                                        <AccordionDescriptionTerm>
                                            {translateKey('common.phoneNumber')}
                                        </AccordionDescriptionTerm>
                                        <AccordionDescriptionDetails>
                                            {props.patient.homePhoneNumber}
                                        </AccordionDescriptionDetails>
                                    </>
                                )}
                                {props.patient.mobileNumber && (
                                    <>
                                        <AccordionDescriptionTerm>
                                            {translateKey('common.mobileTelephoneNumber')}
                                        </AccordionDescriptionTerm>
                                        <AccordionDescriptionDetails>
                                            {props.patient.mobileNumber}
                                        </AccordionDescriptionDetails>
                                    </>
                                )}
                                {props.patient.emailAddress && (
                                    <>
                                        <AccordionDescriptionTerm>
                                            {translateKey('common.email')}
                                        </AccordionDescriptionTerm>
                                        <AccordionDescriptionDetails>
                                            <AccordionLink
                                                href={`mailto:${props.patient.emailAddress}`}
                                                variant="externalLink"
                                            >
                                                {props.patient.emailAddress}
                                            </AccordionLink>
                                        </AccordionDescriptionDetails>
                                    </>
                                )}
                                {(hasOneAuthorization(ManagerAuthorization.patientInsurance) || !isManager) && (
                                    <>
                                        {activeInsurance ? (
                                            <>
                                                <AccordionDescriptionTerm>
                                                    {translateKey('common.healthInsurer')}
                                                </AccordionDescriptionTerm>
                                                <AccordionDescriptionDetails>
                                                    {activeInsurance.uzovi.name}
                                                </AccordionDescriptionDetails>

                                                <AccordionDescriptionTerm>
                                                    {translateKey('patient.insurancePolicyNumber')}
                                                </AccordionDescriptionTerm>
                                                <AccordionDescriptionDetails>
                                                    {activeInsurance.insurancePolicyNumber}
                                                </AccordionDescriptionDetails>
                                            </>
                                        ) : (
                                            <>
                                                <AccordionDescriptionTerm>
                                                    {translateKey('common.healthInsurer')}
                                                </AccordionDescriptionTerm>
                                                <AccordionDescriptionDetails>
                                                    {translateKey('patient.noValidInsurance')}
                                                </AccordionDescriptionDetails>
                                            </>
                                        )}
                                    </>
                                )}
                            </AccordionDescriptionList>
                            {(hasOneAuthorization(ManagerAuthorization.patientEdit) ||
                                props.patient.authorization?.canUpdate) && (
                                <AccordionLink
                                    onClick={async () => setIsEditPatientModalOpen(true)}
                                    variant="button"
                                >
                                    {translateKey('patient.editPatient')}
                                </AccordionLink>
                            )}
                            {props.patient.authorization?.canUpdatePatientConsent &&
                                organizationType === OrganizationType.gpPractice &&
                                (props.patientHasMedicalHistory || hasPatientConsentHistory) && (
                                    <AccordionLink
                                        onClick={props.onClickShowViewMedicalDossierPatientConsentModal}
                                        variant="button"
                                    >
                                        {translateKey('patient.editPatientConsent')}
                                    </AccordionLink>
                                )}
                            {hasOneAuthorization(ManagerAuthorization.patientInsurance) && (
                                <AccordionLink
                                    to={`/patients/${props.patient.id}?overview=insuranceHistory`}
                                    variant="link"
                                >
                                    {`${translateKey('common.view')} ${translateKey(
                                        'insuranceHistory.insuranceHistory'
                                    )}`}
                                </AccordionLink>
                            )}

                            {(!isManager || hasOneAuthorization(ManagerAuthorization.patientProcesses)) &&
                                pdfDownloadButton()}
                        </AccordionContentIndent>
                    </AccordionContent>
                </AccordionItem>

                {props.patient.authorization && props.patient.authorization.canSeeProfessionalSummaryDataDetails && (
                    <>{renderPatientShortLists()}</>
                )}
            </Accordion>
            <Suspense>
                {(props.patient.authorization?.canUpdate || hasOneAuthorization(ManagerAuthorization.patientEdit)) && (
                    <>
                        <PatientEditCareProviderModal
                            isOpen={showEditCareProviderModal}
                            onClose={async () => setShowEditCareProviderModal(false)}
                            onSave={async () => {
                                setShowEditCareProviderModal(false);
                                await props.onReload();
                            }}
                            patient={props.patient}
                        />
                        {isEditPatientModalOpen && (
                            <EditPatientModal
                                isOpen={isEditPatientModalOpen}
                                onClose={async () => setIsEditPatientModalOpen(false)}
                                onSavePatientDetails={async () => props.onReload()}
                                params={{ id: props.patient.id }}
                            />
                        )}
                    </>
                )}
            </Suspense>
        </>
    );

    function renderPatientShortLists() {
        if (
            !props.patient.authorization?.canSeePatientMedication &&
            !props.patient.authorization?.canSeePatientAllergies &&
            !props.patient.authorization?.canSeePatientLabResults &&
            !props.patient.authorization?.canSeePatientEpisodes
        ) {
            return null;
        }

        if (importCycleStatus.isRunning) {
            return <Loader horizontalCentered />;
        }

        return (
            <>
                {props.patient.authorization?.canSeePatientMedication && (
                    <MedicationsShortList patientId={props.patient.id} />
                )}
                {props.patient.authorization?.canSeePatientAllergies && (
                    <AllergiesShortList patientId={props.patient.id} />
                )}
                {props.patient.authorization?.canSeePatientLabResults && (
                    <LabResultsShortList patientId={props.patient.id} />
                )}
                {props.patient.authorization?.canSeePatientEpisodes && (
                    <EpisodeJournalShortList patientId={props.patient.id} />
                )}
            </>
        );
    }

    function pdfDownloadButton() {
        return props.episodeCategory ? renderDownloadButton(props.episodeCategory) : null;
    }

    function renderDownloadButton(episodeCategory: EpisodeCategory) {
        const flexExportPdfEpisodeCategories = [EpisodeCategory.allergology];
        const partialFlexCarePathEpisodeCategoriesRegex = RegExp(`${[EpisodeCategory.somnology].join('|')}`);
        const isFlexExport =
            flexExportPdfEpisodeCategories.includes(episodeCategory) ||
            !!partialFlexCarePathEpisodeCategoriesRegex.exec(location.pathname);

        return (
            <PatientDownloadMedicalRecordOriginal
                episodeCategory={episodeCategory}
                isFlexExport={isFlexExport}
                patientId={props.patient.id}
            />
        );
    }

    async function checkImportStatus() {
        const url = `/api/patients/${props.patient.id}/psJobs`;
        const result = await abortableFetch.fetchJson(url, 'GET');
        const isRunning = result.json.job?.status === JobStatus.running;

        setImportCycleStatus({
            dateTime: DateTime.now(),
            isRunning,
        });
    }

    async function loadCurrentConsent() {
        const consentResponse = await abortableFetch.fetchJson(
            `/api/viewMedicalDossierPatientConsent?organizationId=${organizationId}&patientId=${props.patient.id}`,
            'GET'
        );

        setHasPatientConsentHistory(!isEmpty(consentResponse?.json));
    }

    function stopCheckCycle() {
        if (nextCycleTimer.current) {
            window.clearTimeout(nextCycleTimer.current);
        }
    }
}
